import { BasePagination, PaginableRequest } from 'api/common-schemas';
import {
  AccessProductGridType,
  BaseResponseMobilityAPI,
  CategorizedOfferings,
  DeviceGroupType,
  DeviceType,
  MobilityProduct,
  MobilityProductExtraAttributesType,
  MobilityProductTypes,
  OfferingType,
  ProductCategory,
  ProductDescType,
  ProductType,
  QuestionnaireType,
  QuestionsType,
  TagType,
  ItemSubtype,
  ExtraAttributesOptionType,
  NetworkDeviceType,
  CellularDeviceType,
} from './accessExpress.types';
import { ProductType as AccessProductType } from 'screens/SelectServices/schemas';

import {
  BriefcaseOutline,
  CheckmarkDone,
  FlashOutline,
  ReceiptOutline,
  SpeedometerOutline,
  WarningOutline,
  WifiOutline,
} from 'react-ionicons';
import { apiClient } from 'api/apiClient';
import { ProductType as OfferingProductType } from 'screens/SelectServices/schemas';
import { OrderResponse } from 'api/accessexpressv3/schemas/OrdersSchemas';
import { formatBandwidth, formatTerm } from 'screens/SelectServices/utils';
import { getIpTypeLabel, getLabelForIpBlock } from 'screens/OpenQuote/utils';
import LocationIcon from './BaseComponents/LocationIcon';
import { CartStore, ICartItem, ItemDetails } from './CartProvider';
import {
  CompanyPreferencesResponse,
  ConfigRequirements,
} from './BaseComponents/Cart/locationFlow/access/ConfigRequirementsModal/schema';
import { formatCurrency } from 'shared/util/formatCurrency';
import Starlink from './AccessProductDetails/alternatives/Starlink';
import { ReactComponent as DIAProduct } from 'assets/images/dia_product.svg';
import { ReactComponent as CradlepointProduct } from 'assets/images/cradlepoint_product.svg';
import AlternativeOfferCard from './AccessProductDetails/alternatives/AlternativeOfferCard';
import { useAuthUser } from 'hooks/useAuthUser';
import { postQuote, updateOrder } from 'api/accessexpressv3/api';
import { postQuoteToRequest } from './AccessProductDetails/utils';
import { DIA_REFETCH_INTERVAL } from './AccessProductDetails/AccessProductDetails';
import { toJS } from 'mobx';

type ProductDetails = {
  address: string;
  id: string;
  offerings: AccessProductType[];
  type?: string;
  external_id?: string;
};

export const questionnairesStepOne = [
  {
    type: 'access_services',
    title: 'Access services',
    body: 'Broadband, DIA, GRID',
    tags: ['Access'],
    disabled: true,
  },
  {
    type: 'mobility_services',
    title: 'Mobility services',
    body: 'Wireless routers, smart phones, tablets',
    tags: ['Mobility'],
    disabled: false,
  },
  {
    type: 'voice_services',
    title: 'Voice services',
    body: 'EPOTS, Hosted PBX, POTS',
    tags: ['Voice'],
    disabled: true,
  },
  {
    type: 'not_sure',
    title: 'I’m not sure',
    body: 'Not sure? Let us show you the way',
    tags: [],
    disabled: true,
  },
];

export const questionnairesStepTwo: QuestionnaireType[] = [
  {
    type: 'network',
    title: 'Network',
    body: 'Modems, wireless IoT',
    disabled: true,
  },
  {
    type: 'cellular',
    title: 'Cellular',
    body: 'SIMs, smart phones, tablets, hotspots, and more ',
    disabled: false,
  },
  {
    type: 'accessories',
    title: 'Accessories',
    body: 'Adapters, cases, screen protectors, antennas, batteries, and more',
    disabled: true,
  },
  {
    type: 'sims',
    title: 'SIMs',
    body: 'SIM cards for voice and data connectivity',
    disabled: true,
  },
];

export const getQuestions = async (
  services: string[],
): Promise<QuestionsType> => {
  return new Promise((resolve) => {
    console.log(services);
    resolve({
      title: 'What type of device are you looking for?',
      questions: questionnairesStepTwo,
    });
  });
};

const getSubgroupByType = (type?: string): string => {
  if (!type) return 'DEVICE';

  const [, category] = decodeURIComponent(type).split('|');

  if (category === 'ACCESSORY') return 'ACCESSORY';

  return (
    Object.keys(DeviceGroupType).find((group) =>
      DeviceGroupType[group as keyof typeof DeviceGroupType].includes(
        decodeURIComponent(type) as DeviceType,
      ),
    ) ?? 'DEVICE'
  );
};

export const parseDeviceParam = (param: string) => {
  const [type, category] = param.split('|');

  return {
    type: type || null,
    category: category || null,
  };
};

export const getMobilityProducts = async (args?: {
  type: string;
}): Promise<BaseResponseMobilityAPI<ProductType[]>> => {
  const { type } = parseDeviceParam(decodeURIComponent(args?.type || ''));

  const subgroup = getSubgroupByType(args?.type);

  const response = await apiClient.get<
    BaseResponseMobilityAPI<MobilityProduct[]>
  >(
    `/api/v1/marketplace/products/all?subgroup=${subgroup}${
      type ? `&type=${type}` : ''
    }`,
  );

  return {
    ...response.data,
    data: response.data.data.map((product) => ({
      id: product.graniteId,
      title: product.graniteId,
      img: product.images[0]?.url
        ? encodeURI(product.images[0]?.url).replace('+', '%2B')
        : null,
      manufacturer: product.graniteId.trim().split(/\s+/)[0],
      stock: product.productQty,
      content: product.description,
      createdDate: product.createdDate,
      productDescType: product.productDesc,
    })),
  };
};

export const getProduct = async (args: {
  id: string;
}): Promise<ProductType> => {
  const { id } = args;

  const response = await apiClient.get<
    BaseResponseMobilityAPI<MobilityProduct>
  >(`/api/v1/marketplace/products/detail?alias=${encodeURIComponent(id)}`);
  return {
    id: response.data.data.graniteId,
    title: response.data.data.graniteId,
    img: response.data.data.images[0]?.url ?? null,
    manufacturer: response.data.data.graniteId.trim().split(/\s+/)[0],
    stock: response.data.data.productQty,
    content: response.data.data.description,
    createdDate: response.data.data.createdDate,
    varients: response.data.data.variants,
    productId: response.data.data.productId,
    productDescType: response.data.data.productDesc,
  };
};

export const getStarlinkOfferDetails = (
  offering: OfferingProductType,
  tag: string,
): {
  id: string;
  tag: TagType;
  price: number;
  carrier?: string;
  details: {
    icon: React.ElementType | JSX.Element;
    text: string;
    id: string;
  }[];
} => {
  const pricing = offering?.formal_starlink_pricing;
  const price = pricing?.total_mrc ?? 0;

  return {
    id: offering.id,
    tag: tag as TagType,
    price: price,
    carrier:
      offering.service_level_tier === 'PRIMARY'
        ? 'Primary plan'
        : offering.service_level_tier === 'BACKUP'
          ? 'Backup plan'
          : undefined,
    details: [
      {
        icon: BriefcaseOutline,
        text: `Enterprise bundle`,
        id: 'enterprise_bundle',
      },
      {
        icon: ReceiptOutline,
        text: `Month-to-month`,
        id: 'term',
      },
      {
        icon: SpeedometerOutline,
        text: offering.data_plan ?? '',
        id: 'data_plan',
      },
      {
        icon: LocationIcon,
        id: 'ip_type',
        text: 'Dynamic IP',
      },
      {
        icon: FlashOutline,
        id: 'activation',
        text: `${formatCurrency(pricing?.activation ?? 0)} activation fee`,
      },
      {
        icon: CheckmarkDone,
        id: 'installation',
        text: `Install add-on*`,
      },
    ],
  };
};

export const getOfferDetails = (offering: OfferingProductType, tag: string) => {
  const dia = offering.type === 'dia';

  const price =
    offering?.formal_broadband_pricing?.total_mrc ??
    offering?.formal_dia_pricing?.total_mrc ??
    0;

  const speed = dia
    ? //@ts-expect-error err
      `${formatBandwidth(offering?.bandwidth)}/${formatBandwidth(
        //@ts-expect-error err
        offering?.bandwidth,
      )}`
    : `${formatBandwidth(offering?.bandwidth_download)}/${formatBandwidth(
        offering?.bandwidth_upload,
      )}`;
  return {
    id: offering.id,
    tag: tag as TagType,
    price: price,
    construction_likely: offering.construction_likely,
    carrier: offering.provider,
    details: [
      {
        icon: SpeedometerOutline,
        text: speed,
        id: 'speed',
      },
      {
        icon: ReceiptOutline,
        text: `${formatTerm(offering.term!)}`,
        id: 'term',
      },
      ...(offering.type === 'broadband'
        ? [
            {
              icon: LocationIcon,
              id: 'ip_type',
              text: getIpTypeLabel(offering.ip_type),
            },
          ]
        : []),
      ...(offering.construction_likely
        ? [
            {
              id: 'construction_likely',
              icon: WarningOutline,
              text: CONSTRUCTION_LABEL,
            },
          ]
        : []),
      ...(offering?.on_net
        ? [
            {
              icon: WifiOutline,
              id: 'on_net',
              text: `${offering?.on_net ? 'On' : 'Off'}-net connectivity`,
            },
          ]
        : []),
    ],
  };
};

export const categorizeOfferings = (
  category: ProductCategory,
  product?: ProductDetails,
): CategorizedOfferings | undefined => {
  if (!product)
    return {
      bestOffer: undefined,
      premiumChoice: undefined,
      lowestPrice: undefined,
      allOtherOptions: [],
    };

  if (category === 'Starlink') {
    const bestOffer = product.offerings?.find(
      (offering) => offering.service_level_tier === 'PRIMARY',
    );
    const otherOption = product.offerings?.find(
      (offering) => offering.service_level_tier === 'BACKUP',
    );
    return {
      bestOffer,
      bestOfferDetails: bestOffer
        ? getStarlinkOfferDetails(bestOffer, 'Best offer')
        : undefined,
      otherOption,
      otherOptionDetails: otherOption
        ? getStarlinkOfferDetails(otherOption, 'Other option')
        : undefined,

      premiumChoice: undefined,
      premiumChoiceDetails: undefined,
      lowestPrice: undefined,
      lowestPriceDetails: undefined,
      allOtherOptions: product.offerings ?? [],
      sortedOfferings: product.offerings ?? [],
    };
  }
  if (category === 'Broadband' || category === 'DIA') {
    const offerings = [...(product?.offerings || [])].sort((a, b) =>
      a.priority > b.priority ? 1 : -1,
    );
    // TODO: remove ts-expect-error comments once correct type is used
    const maxOffer = offerings.reduce((max, current) => {
      switch (category) {
        case 'Broadband':
          // @ts-expect-error err
          return current?.formal_broadband_pricing?.total_mrc >
            // @ts-expect-error err
            max?.formal_broadband_pricing?.total_mrc
            ? current
            : max;
        case 'DIA':
          // @ts-expect-error err
          return current?.formal_dia_pricing?.total_mrc >
            // @ts-expect-error err
            max?.formal_dia_pricing?.total_mrc
            ? current
            : max;
      }
    }, offerings[0]);

    const minOffer = offerings.reduce((min, current) => {
      switch (category) {
        case 'Broadband':
          // @ts-expect-error err
          return current?.formal_broadband_pricing?.total_mrc <
            // @ts-expect-error err
            min?.formal_broadband_pricing?.total_mrc
            ? current
            : min;
        case 'DIA':
          // @ts-expect-error err
          return current?.formal_dia_pricing?.total_mrc <
            // @ts-expect-error err
            min?.formal_dia_pricing?.total_mrc
            ? current
            : min;
      }
    }, offerings[0]);

    let bestOffer = offerings[0];
    for (const offer of offerings) {
      if (offer.id !== maxOffer.id && offer.id !== minOffer.id) {
        bestOffer = offer;
        break;
      }
    }

    const allOtherOptions = offerings.filter(
      (offer) => ![bestOffer.id, maxOffer.id, minOffer.id].includes(offer.id),
    );
    return {
      bestOffer: bestOffer,
      premiumChoice: maxOffer,
      lowestPrice: minOffer,
      allOtherOptions: allOtherOptions,
      sortedOfferings: product?.offerings,
      bestOfferDetails: bestOffer
        ? getOfferDetails(bestOffer, 'Best offer')
        : undefined,
      premiumChoiceDetails: maxOffer
        ? getOfferDetails(maxOffer, 'Premium choice')
        : undefined,
      lowestPriceDetails: minOffer
        ? getOfferDetails(minOffer, 'Lowest price')
        : undefined,
      otherOption: undefined,
      otherOptionDetails: undefined,
    };
  }

  return {
    bestOffer: undefined,
    bestOfferDetails: undefined,
    premiumChoice: undefined,
    premiumChoiceDetails: undefined,
    lowestPrice: undefined,
    lowestPriceDetails: undefined,
    allOtherOptions: [],
    sortedOfferings: undefined,
    otherOption: undefined,
    otherOptionDetails: undefined,
  };
};

export const broadbandMetadata = {
  id: '1',
  type: 'broadband',
  title: 'Broadband',
  content:
    'Broadband services provide high-speed internet access, enabling connectivity to the internet for activities like browsing, streaming, downloading, and more. Order fiber or cable to deliver fast and reliable internet connectivity to your business.',
};

export const getAccessProductsGrid = async (
  args: PaginableRequest & { search?: string; sort_order?: 'asc' | 'desc' },
): Promise<BasePagination<ProductType>> => {
  const { page = 1, page_size = 12, search, sort_order } = args;

  const response = await apiClient.get<{
    data: AccessProductGridType[];
    page: number;
    page_size: number;
    total: number;
  }>(
    `/api/v1/access?page=${page}&page_size=${page_size}${
      search ? `&search=${search}` : ''
    }${sort_order ? `&sort_field=name&sort_order=${sort_order}` : ''}`,
  );

  const res = {
    data: response.data.data.map((product) => ({
      id: `${product.id}`,
      title: product.name,
      fullPriceInfo: product.description,
      img: product.image_url,
      category: product.category,
    })),
    metadata: {
      page: response.data.page,
      page_size: response.data.page_size,
      total_items: response.data.total,
      total_pages: Math.ceil(response.data.total / response.data.page_size),
    },
  };
  return res;
};

export const getMobilityProductTypes = async (): Promise<
  BaseResponseMobilityAPI<MobilityProductTypes>
> => {
  const response = await apiClient.get('/api/v1/marketplace/products/types');
  return response.data;
};

export const formatMobilityPrice = (price?: number) =>
  price !== undefined ? price.toFixed(2) : '0.00';

export const checkoutDummyAddresses = [
  {
    account_name: '04683713',
    address_line_1: '12419 Shelbyville Rd',
    address_line_2: '12',
    city: 'Middletown',
    id: '73585',
    macnum: '04683713',
    name: '04683713 - 40132',
    parent_macnum: '03479292',
    parent_name: 'Valvoline',
    site_name: '40132',
    state: 'KY',
    zip: '40243',
    cw_site_id: '001233689',
  },
  {
    account_name: '04683717',
    address_line_1: '1861 Gardner Lane NW',
    address_line_2: null,
    city: 'Corydon',
    id: '73588',
    macnum: '04683717',
    name: '04683717 - 40136',
    parent_macnum: '03479292',
    parent_name: 'Valvoline',
    site_name: '40136',
    state: 'IN',
    zip: '47112',
    cw_site_id: '001233688',
  },
  {
    account_name: '04683730',
    address_line_1: '2976 S Church Street',
    address_line_2: null,
    city: 'Murfreesboro',
    id: '73603',
    macnum: '04683730',
    name: '04683730 - 40157',
    parent_macnum: '03479292',
    parent_name: 'Valvoline',
    site_name: '40157',
    state: 'TN',
    zip: '37127',
    cw_site_id: '001233687',
  },
  {
    account_name: '04683731',
    address_line_1: '7065 E US Hwy 36',
    address_line_2: null,
    city: 'Avon',
    id: '73604',
    macnum: '04683731',
    name: '04683731 - 40158',
    parent_macnum: '03479292',
    parent_name: 'Valvoline',
    site_name: '40158',
    state: 'IN',
    zip: '46123',
    cw_site_id: '001233686',
  },
  {
    account_name: '04683752',
    address_line_1: '10229 Watson Road',
    address_line_2: null,
    city: 'Sappington',
    id: '73624',
    macnum: '04683752',
    name: '04683752 - 50009',
    parent_macnum: '03479292',
    parent_name: 'Valvoline',
    site_name: '50009',
    state: 'MO',
    zip: '63127',
    cw_site_id: null,
  },
];

export const checkoutOrderItems = [
  {
    type: 'Access',
    subtype: 'Broadband',
    pim_price_id: 'PIM1234',
    quantity: 2,
    location_id: 'loc-001',
    item_details: {
      detail1: 'value1',
      detail2: 'value2',
    },
    price: 49.99,
  },
  {
    type: 'Mobility',
    subtype: 'Phone',
    pim_price_id: 'PIM5678',
    quantity: 1,
    location_id: 'loc-001',
    item_details: {
      model: 'XYZ',
      color: 'black',
    },
    price: 99.99,
  },
  {
    type: 'Sims',
    subtype: 'Standard',
    pim_price_id: 'PIM9012',
    quantity: 1,
    location_id: 'loc-001',
    item_details: {
      carrier: 'ABC',
    },
    price: 19.99,
  },
];

export const getDistinctAccessOrderedServices = (data?: OrderResponse) => {
  if (!data) return '';
  const orderedProducts = new Set();

  data.locations?.forEach((location) => {
    location.order_items.forEach((item) => {
      if (['broadband', 'dia'].includes(item.subtype)) {
        const category =
          item.subtype === 'broadband'
            ? 'Broadband'
            : item.subtype === 'dia'
              ? 'DIA'
              : '';
        orderedProducts.add(category);
      }
    });
  });

  return Array.from(orderedProducts).join(', ');
};

export const CONSTRUCTION_LABEL = 'Potential construction';

export const getMNSPrice = (
  productOffering: AccessProductType,
  autoSelect = false,
) => {
  const productPricingPath =
    productOffering?.type === 'broadband'
      ? productOffering?.formal_broadband_pricing
      : productOffering?.type === 'dia'
        ? productOffering?.formal_dia_pricing
        : null;

  if (!productPricingPath) return 0;

  if (autoSelect) {
    return (
      (productOffering?.type === 'broadband'
        ? productPricingPath?.mns_essentials
        : productOffering?.type === 'dia'
          ? productPricingPath?.mns_enhanced
          : 0) ?? 0
    );
  }

  return (
    (productOffering?.mns_enhanced
      ? productPricingPath?.mns_enhanced
      : productOffering.mns_essential
        ? productPricingPath?.mns_essentials
        : 0) ?? 0
  );
};

export const getOfferingPrice = (
  productOffering: AccessProductType,
  withoutMNS?: boolean,
) => {
  const productPricingPath =
    productOffering?.type === 'broadband'
      ? productOffering?.formal_broadband_pricing
      : productOffering?.type === 'dia'
        ? productOffering?.formal_dia_pricing
        : productOffering?.type === 'starlink'
          ? productOffering?.formal_starlink_pricing
          : null;

  if (!productPricingPath) return 0;

  if (withoutMNS) {
    // @ts-expect-error err
    return productPricingPath?.total_mrc_without_mns ?? 0;
  }

  return productPricingPath?.total_mrc ?? 0;
};

export const downloadOptions = [
  { label: '10 Mbps', value: '10' },
  { label: '20 Mbps', value: '20' },
  { label: '50 Mbps', value: '50' },
  { label: '100 Mbps', value: '100' },
  { label: '250 Mbps', value: '250' },
  { label: '500 Mbps', value: '500' },
  { label: '700 Mbps', value: '700' },
  { label: '1000 Mbps', value: '1000' },
];

export const termOptions = [
  { label: '1 year', value: '1' },
  { label: '2 years', value: '2' },
  { label: '3 years', value: '3' },
];

export const ipBlockOptions = [
  { label: '/29', value: '/29' },
  { label: '/30', value: '/30' },
];

export const ipTypeOptions = [
  { label: 'Static', value: 'static' },
  { label: 'Dynamic', value: 'dynamic' },
];

export const preferredCarrierOptions = [
  { label: 'cincinnati bell', value: 'altafiber' },
  { label: 'armstrong', value: 'armstrong granite upload' },
];

export const carrierOptions = [
  { label: 'AT&T', value: 'AT&T' },
  { label: 'Alaska Communications', value: 'Alaska Communications' },
  { label: 'Altice/Optimum', value: 'Altice/Optimum' },
  { label: 'Armstrong', value: 'Armstrong' },
  { label: 'Atlantic Broadband', value: 'Atlantic Broadband' },
  { label: 'Brightspeed', value: 'Brightspeed' },
  { label: 'Cable One', value: 'Cable One' },
  { label: 'CenturyLink/Lumen', value: 'CenturyLink/Lumen' },
  { label: 'Cincinnati Bell/AltaFiber', value: 'Cincinnati Bell/AltaFiber' },
  { label: 'Comcast', value: 'Comcast' },
  { label: 'Cox', value: 'Cox' },
  { label: 'Fairpoint/Consolidated', value: 'Fairpoint/Consolidated' },
  { label: 'Fidelity', value: 'Fidelity' },
  { label: 'Frontier', value: 'Frontier' },
  { label: 'GTT', value: 'GTT' },
  { label: 'Granite Rural Broadband', value: 'Granite Rural Broadband' },
  {
    label: 'Great Plains Communications',
    value: 'Great Plains Communications',
  },
  { label: 'Hargray', value: 'Hargray' },
  { label: 'Hunter', value: 'Hunter' },
  { label: 'Liberty PR', value: 'Liberty PR' },
  { label: 'Mediacom', value: 'Mediacom' },
  { label: 'MetroNet', value: 'MetroNet' },
  { label: 'Midco', value: 'Midco' },
  { label: 'Nex-Tech', value: 'Nex-Tech' },
  { label: 'PenTeleData', value: 'PenTeleData' },
  { label: 'Ritter', value: 'Ritter' },
  { label: 'Shentel/GLoFiber', value: 'Shentel/GLoFiber' },
  { label: 'Spectrum', value: 'Spectrum' },
  { label: 'Verizon', value: 'Verizon' },
  { label: 'Vyve', value: 'Vyve' },
  { label: 'WOW!', value: 'WOW!' },
  { label: 'Windstream', value: 'Windstream' },
  { label: 'Ziply Fiber', value: 'Ziply Fiber' },
];

export const buildConfigRequirementsModalDefaults = (
  companyPreferences?: Partial<CompanyPreferencesResponse>,
  cart?: CartStore,
): Partial<ConfigRequirements> | undefined => {
  if (!companyPreferences || !cart) return undefined;

  return {
    term: companyPreferences.term
      ? {
          value: companyPreferences.term,
          label: termOptions.find(
            (item) => item.value === companyPreferences.term,
          )?.label as string,
        }
      : termOptions[2],
    download: companyPreferences.max_download
      ? {
          value: companyPreferences.max_download,
          label: downloadOptions.find(
            (item) => item.value === companyPreferences.max_download,
          )?.label as string,
        }
      : undefined,
    preferred_carriers: companyPreferences.preferred_carriers?.map((c) => ({
      value: c,
      label: c,
    })),
    exclude_carriers: companyPreferences.exclude_carriers?.map((c) => ({
      value: c,
      label: c,
    })),
    ip_type:
      cart.selectedAccessProductCategory === 'DIA'
        ? ipTypeOptions.find((opt) => opt.value === 'static')
        : cart.selectedAccessProductCategory === 'Broadband' ||
            cart.selectedAccessProductCategory === 'All'
          ? ipTypeOptions.find((opt) => opt.value === 'dynamic')
          : companyPreferences.ip_type
            ? {
                value: companyPreferences.ip_type,
                label: ipTypeOptions.find(
                  (opt) => opt.value === companyPreferences.ip_type,
                )?.label as string,
              }
            : undefined,
    ip_block: companyPreferences.ip_block
      ? {
          value: companyPreferences.ip_block,
          label: ipBlockOptions.find(
            (opt) => opt.value === companyPreferences.ip_block,
          )?.label as string,
        }
      : undefined,
    save_default: false,
  };
};

type OfferContext = {
  tag: TagType;
  category: ProductCategory;
  offering: OfferingType | undefined;
  isLoading: boolean;
  isAllProducts?: boolean;
};

export type AlternativeOfferType =
  | 'Broadband'
  | 'DIA'
  | 'Starlink'
  | 'cradlepoint'
  | null;

export const getAlternativeOfferType = ({
  tag,
  category,
  offering,
  isLoading,
  isAllProducts,
}: OfferContext): AlternativeOfferType => {
  if (isLoading || offering) return null;

  const fallbackMatrix: Record<ProductCategory, AlternativeOfferType[]> = {
    Broadband: ['Starlink', 'DIA', 'cradlepoint'],
    DIA: isAllProducts
      ? ['Starlink', 'Broadband', 'cradlepoint']
      : ['Broadband', 'Starlink', 'cradlepoint'],
    Starlink: ['Broadband', 'DIA', 'cradlepoint'],
    'Granite MNS': [null, null, null], // tbd
    All: [null, null, null], // tbd
  };

  const indexByTag: Record<TagType, number> = {
    'Best offer': 0,
    'Premium choice': 1,
    'Lowest price': 2,
    'Other option': 2,
  };

  const fallbackIndex = indexByTag[tag];
  return fallbackMatrix[category]?.[fallbackIndex] ?? null;
};

export const AlternativeComponents: Record<
  NonNullable<AlternativeOfferType>,
  JSX.Element
> = {
  Starlink: <Starlink />,
  DIA: (
    <AlternativeOfferCard
      carrier="DIA"
      price="$300"
      image={DIAProduct}
      type="DIA"
    />
  ),
  Broadband: (
    <AlternativeOfferCard
      carrier="Broadband"
      price="$300"
      image={DIAProduct}
      type="Broadband"
    />
  ),
  cradlepoint: (
    <AlternativeOfferCard
      carrier="Wireless broadband"
      price="$99.99"
      image={CradlepointProduct}
      type="cradlepoint"
    />
  ),
};

export const getAccessProductName = (item: ICartItem['item_details']) => {
  if (item) {
    if (item.type === 'broadband' || item.type === 'dia') {
      const download = String(
        item?.bandwidth_download ?? item?.bandwidth ?? '',
      );
      const upload = String(item?.bandwidth_upload ?? item?.bandwidth ?? '');
      const speed = `${formatBandwidth(download)}/${formatBandwidth(upload)}`;
      const name = item.type === 'dia' ? 'DIA' : 'Broadband';
      return `${name} - ${item.provider ?? ''} ${speed}`;
    }
    if (item.type === 'starlink') {
      return getStarlinkCircuitName(item?.service_level_tier, item?.bundle);
    }
  }
  return (item?.name as string) ?? (item?.graniteId as string) ?? '';
};

export const getStarlinkCircuitName = (
  tier?: string,
  bundle?: string,
): string => {
  const tierLabel =
    tier === 'PRIMARY' ? 'Primary' : tier === 'BACKUP' ? 'Backup' : '';

  const bundleLabel =
    bundle === 'enterprise_bundle'
      ? 'Enterprise'
      : bundle === 'mini_bundle'
        ? 'Mini'
        : bundle === 'hperf_bundle'
          ? 'High Performance'
          : '';

  return `Starlink - ${tierLabel} ${bundleLabel}`;
};

export const handleAccessCategorySelection = async ({
  type,
  cart,
  userInfo,
  setStartPolling,
  setLoadingDraftQuote,
}: {
  type: ProductCategory;
  cart: CartStore;
  userInfo: ReturnType<typeof useAuthUser>;
  setStartPolling: (val: boolean) => void;
  setLoadingDraftQuote: (val: boolean) => void;
}) => {
  cart.selectAccessProductCategory(type);
  if (type === 'DIA') cart.setDIAFlowActive(true);

  try {
    const quoteResponse = await postQuote(
      postQuoteToRequest(userInfo, cart.locations, [
        {
          product_type: type,
          ...cart.additionalDetails?.configs,
        },
      ]),
    );

    const additionalDetails = {
      configs: cart.additionalDetails?.configs,
      ...(type === 'Starlink'
        ? { starlink_quote_request: quoteResponse.id }
        : type === 'DIA'
          ? { dia_quote_request: quoteResponse.id }
          : { bb_quote_request: quoteResponse.id }),
    };

    const draft = await updateOrder(
      { additional_details: additionalDetails },
      cart.draftId ?? '',
    );

    cart.updateStateFromDraft(draft);

    handlePostQuoteStatus({
      category: type,
      status: quoteResponse.status,
      setStartPolling,
      setLoadingDraftQuote,
    });

    cart.setCartState('default');
  } catch (err) {
    console.error('Error creating quote:', err);
    setLoadingDraftQuote(false);
  }
};

const handlePostQuoteStatus = ({
  category,
  status,
  setStartPolling,
  setLoadingDraftQuote,
}: {
  category: ProductCategory;
  status: string;
  setStartPolling: (val: boolean) => void;
  setLoadingDraftQuote: (val: boolean) => void;
}) => {
  if (category === 'DIA') {
    if (status === 'Checking availability') {
      setTimeout(() => {
        setStartPolling(true);
      }, DIA_REFETCH_INTERVAL);
    } else if (status === 'Closed') {
      setStartPolling(false);
      setLoadingDraftQuote(false);
    }
  } else {
    setLoadingDraftQuote(false);
  }
};

export const getMobilityProductExtraAttributes = async ({
  pimProductPricingId,
}: {
  pimProductPricingId: number;
}): Promise<MobilityProductExtraAttributesType> => {
  const response = await apiClient.get(
    `/api/v1/marketplace/extra/${pimProductPricingId}`,
  );
  return response.data;
};

export function getProductCategory(desc?: ProductDescType): ItemSubtype | '' {
  switch (desc) {
    case 'SMART PHONE':
    case 'BASIC CELL PHONE':
      return ItemSubtype.SMARTPHONE;
    case 'TABLET':
      return ItemSubtype.TABLET;
    case 'SIM CARD':
      return ItemSubtype.SIM;
    case 'HOTSPOT':
    case 'MODEM':
      return ItemSubtype.MODEM;
    case 'WIRELESS ROUTER':
      return ItemSubtype.ROUTER;
    case 'PLAN':
      return ItemSubtype.PLAN;
    case 'DATA':
      return ItemSubtype.DATA;
    default:
      return '';
  }
}

export type MobilityProductType = AccessProductType & {
  bundle?: string;
  dataPlanNeeded?: {
    neededPlan: string;
    activation: string;
  };
  pricingOption?: string;
  keyFeature?: string;
  subtype?: string;
  productDescType?: ProductDescType;
  planTypeOption?: ExtraAttributesOptionType;
  simOption?: ExtraAttributesOptionType;
  additionalPlanTypeOption?: ExtraAttributesOptionType;
  additionalPlanType?: string;
  planType?: string;
  simType?: string;
  carrierName?: string;
  additionalCarrierName?: string;
};

export const getAccessProductSummaryItems = (item: ICartItem) => {
  const product = item?.item_details as AccessProductType;
  const recurringCharges = getRecurringCharges([item]);
  const nonRecurringCharges = getNonRecurringCharges([item]);

  const addCharge = (
    items: Array<{ label: string; value: React.ReactNode; category: string }>,
    charges: {
      grandTotal: number;
      charges: {
        name: string;
        price: number;
      }[];
    },
    chargeName: string,
    label: string,
    category: string,
    forceDisplay?: boolean,
    recurring = true,
  ) => {
    const charge = charges.charges.find((charge) => charge.name === chargeName)
      ?.price;
    if (
      (charge !== undefined || forceDisplay === true) &&
      (charge !== 0 || forceDisplay !== false)
    ) {
      items.push({
        label,
        value: formatCurrency(charge) + (recurring && charge ? ' / month' : ''),
        category,
      });
    }
  };

  const items: Array<{
    label: string;
    value: React.ReactNode;
    category: string;
  }> = [];

  if (product.type === 'broadband' || product.type === 'dia') {
    addCharge(
      items,
      recurringCharges,
      'DIA connection charge',
      'Connection',
      'Recurring charges',
    );
    addCharge(
      items,
      recurringCharges,
      'Broadband connection charge',
      'Connection',
      'Recurring charges',
    );
    if (product.type === 'broadband') {
      addCharge(
        items,
        recurringCharges,
        'Equipment',
        'Modem',
        'Recurring charges',
        true,
      );
    }
    if (product.ip_type === 'STATIC') {
      items.push({
        label: 'IP block size',
        value: `${product.ip_block} - ${
          getLabelForIpBlock(product.ip_block || '') || 'N/A'
        }`,
        category: 'Recurring charges',
      });
    }
    addCharge(
      items,
      recurringCharges,
      'IP blocks',
      'IP block cost',
      'Recurring charges',
      product.ip_type === 'STATIC' ? undefined : false,
    );
    addCharge(
      items,
      recurringCharges,
      'Carrier surcharge recovery',
      'Carrier surcharge recovery (CSR)',
      'Recurring charges',
    );
    addCharge(
      items,
      recurringCharges,
      'Internet access recovery',
      'Internet access recovery (IAR)',
      'Recurring charges',
    );
    addCharge(
      items,
      recurringCharges,
      'Network monitoring',
      'Network monitoring',
      'Recurring charges',
      false,
    );
    addCharge(
      items,
      nonRecurringCharges,
      'Activation fee',
      'Activation fee',
      'Non-recurring charges',
      false,
      false,
    );
  }

  if (product.type === 'starlink') {
    addCharge(
      items,
      recurringCharges,
      'Main service plan',
      'Service plan',
      'Recurring charges',
    );
    addCharge(
      items,
      nonRecurringCharges,
      'Equipment bundle',
      'Equipment',
      'Non-recurring charges',
      true,
      false,
    );
    addCharge(
      items,
      nonRecurringCharges,
      'Starlink activation fee',
      'Activation fee',
      'Non-recurring charges',
      undefined,
      false,
    );
    addCharge(
      items,
      nonRecurringCharges,
      'Install add-on',
      'Install',
      'Non-recurring charges',
      false,
      false,
    );
  }

  return items;
};

export const getMobilityMRCAndNRC = ({
  device,
  plan,
  sim,
  secondaryPlan,
}: {
  device?: ItemDetails;
  plan?: ExtraAttributesOptionType;
  sim?: ExtraAttributesOptionType;
  secondaryPlan?: ExtraAttributesOptionType;
}) => {
  if (device) {
    plan = device?.planTypeOption as ExtraAttributesOptionType;
    sim = device?.simOption as ExtraAttributesOptionType;
    secondaryPlan =
      device?.additionalPlanTypeOption as ExtraAttributesOptionType;
  }

  const deviceMRC =
    device?.productGroupDesc === 'FINANCED'
      ? Number(device?.standardRate ?? 0)
      : 0;
  const deviceNRC =
    device?.productGroupDesc === 'PURCHASED'
      ? Number(device?.standardRate ?? 0)
      : 0;

  const planMRC = plan?.standard_rate ?? 0;
  const planNRC = 0;

  const simMRC = sim?.standard_rate ?? 0;
  const simNRC = 0;

  const secondaryPlanMRC = secondaryPlan?.standard_rate ?? 0;
  const secondaryPlanNRC = 0;

  const totalMRC = deviceMRC + planMRC + simMRC + secondaryPlanMRC;
  const totalNRC = deviceNRC + planNRC + simNRC + secondaryPlanNRC;

  return {
    device: {
      mrc: deviceMRC,
      nrc: deviceNRC,
    },
    plan: {
      mrc: planMRC,
      nrc: planNRC,
    },
    secondaryPlan: {
      mrc: secondaryPlanMRC,
      nrc: secondaryPlanNRC,
    },
    sim: {
      mrc: simMRC,
      nrc: simNRC,
    },
    total: {
      mrc: totalMRC,
      nrc: totalNRC,
    },
  };
};

export const getMobilityProductSummaryItems = (item: ICartItem) => {
  const product = item.item_details as MobilityProductType | undefined;

  function extractCarrier(compatible_provider: string): string {
    try {
      const providers = JSON.parse(compatible_provider);
      return providers.length > 0 ? providers[0] : '';
    } catch {
      return '';
    }
  }

  const MRCAndNRC = getMobilityMRCAndNRC({ device: product });

  const items = [
    (product?.subtype === 'Tablet' || product?.subtype === 'Smartphone') &&
      product?.productDescType !== DeviceType.basicPhone && {
        label: 'Option',
        value: product?.keyFeature,
      },
    {
      label: 'Data plan',
      value:
        product?.dataPlanNeeded?.neededPlan === 'yes' ? 'Active' : 'Inactive',
    },
    product?.pricingOption && {
      label: 'Pricing option',
      value: product?.pricingOption,
    },
    (MRCAndNRC.device.mrc || MRCAndNRC.device.nrc) && {
      label: 'Equipment cost',
      value: MRCAndNRC.device.mrc
        ? `${formatCurrency(MRCAndNRC.device.mrc)} / month`
        : formatCurrency(MRCAndNRC.device.nrc),
    },
    product?.simType && {
      label: 'SIM type',
      value: product?.simType,
    },
    (MRCAndNRC.plan.mrc || MRCAndNRC.plan.nrc) && {
      label: 'Plan cost',
      value: MRCAndNRC.plan.mrc
        ? `${formatCurrency(MRCAndNRC.plan.mrc)} / month`
        : formatCurrency(MRCAndNRC.plan.nrc),
    },
    (product?.planTypeOption?.compatible_provider || product?.carrierName) && {
      label: 'Plan vendor',
      value:
        product.carrierName ??
        extractCarrier(product.additionalPlanTypeOption!.compatible_provider),
    },
    product?.planType && {
      label: 'Plan type',
      value: product?.planType,
    },

    (MRCAndNRC.secondaryPlan.mrc || MRCAndNRC.secondaryPlan.nrc) && {
      label: 'Secondary plan cost',
      value: MRCAndNRC.secondaryPlan.mrc
        ? `${formatCurrency(MRCAndNRC.secondaryPlan.mrc)} / month`
        : formatCurrency(MRCAndNRC.secondaryPlan.nrc),
    },
    (product?.additionalCarrierName ||
      product?.additionalPlanTypeOption?.compatible_provider) && {
      label: 'Secondary plan vendor',
      value:
        product.additionalCarrierName ??
        extractCarrier(product.additionalPlanTypeOption!.compatible_provider),
    },
    product?.additionalPlanType && {
      label: 'Secondary plan type',
      value: product?.additionalPlanType,
    },
  ].filter(Boolean) as Array<{ label: string; value: React.ReactNode }>;

  return items;
};

export const hasMobilityItems = (items: ICartItem[]) =>
  items.some((item) => item.category === 'Mobility');

export const filterItemsByCategory = (
  items: ICartItem[],
  category: ICartItem['category'],
) => {
  if (category === 'Mobility') {
    return items.filter(
      (item) => item.category === category && !item.item_details?.parentId,
    );
  }
  return items.filter((item) => item.category === category);
};

export const getServiceList = (items: ICartItem[]) => {
  const tempServices: string[] = [];
  items.forEach((item) => {
    if (
      item.item_details?.product_type &&
      !tempServices.includes(item.item_details?.product_type as string)
    )
      tempServices.push(item.item_details?.product_type as string);
    if (
      item.type !== 'Access' &&
      item.subtype &&
      !tempServices.includes(item.subtype as string)
    ) {
      tempServices.push(item.item_details?.subtype as string);
    }
  });
  return tempServices;
};

export const getMobilityFormData = (allItems: ICartItem[]) => {
  const items = filterItemsByCategory(allItems, 'Mobility') ?? [];

  const products = items.map((item) => {
    const total = getMobilityMRCAndNRC({ device: item.item_details }).total;
    return {
      ...item,
      item_details: {
        ...item.item_details,
        totalMRC: total.mrc,
        totalNRC: total.nrc,
      },
    };
  });

  return {
    products,
    nrc: [
      ...items
        .filter((item) => item.item_details?.referenceId)
        .map((item) => ({
          name: item.name,
          price:
            getMobilityMRCAndNRC({ device: item.item_details }).device.nrc *
            item.quantity,
          quantity: item.quantity,
        })),
    ].filter((item) => !!item.price),
    mrc: [
      ...items
        .filter((item) => item.item_details?.referenceId)
        .map((item) => ({
          name: item.name,
          price:
            getMobilityMRCAndNRC({ device: item.item_details }).device.mrc *
            item.quantity,
          quantity: item.quantity,
        })),
      {
        name: 'Data plan',
        price: items
          .map(
            (item) =>
              getMobilityMRCAndNRC({
                plan: item?.item_details
                  ?.planTypeOption as ExtraAttributesOptionType,
              }).plan.mrc * item.quantity,
          )
          .reduce((acc, curr) => acc + curr, 0),
        quantity: items
          .filter((item) => item.item_details?.planTypeOption)
          .map((item) => item.quantity)
          .reduce((acc, curr) => acc + curr, 0),
      },
      {
        name: 'Secondary data plan',
        price: items
          .map(
            (item) =>
              getMobilityMRCAndNRC({
                secondaryPlan: item?.item_details
                  ?.additionalPlanTypeOption as ExtraAttributesOptionType,
              }).secondaryPlan.mrc * item.quantity,
          )
          .reduce((acc, curr) => acc + curr, 0),
        quantity: items
          .filter((item) => item.item_details?.additionalPlanTypeOption)
          .map((item) => item.quantity)
          .reduce((acc, curr) => acc + curr, 0),
      },
      {
        name: 'SIM',
        price: items
          .map(
            (item) =>
              getMobilityMRCAndNRC({
                sim: item?.item_details?.simOption as ExtraAttributesOptionType,
              }).sim.mrc * item.quantity,
          )
          .reduce((acc, curr) => acc + curr, 0),
        quantity: items
          .filter((item) => item.item_details?.simOption)
          .map((item) => item.quantity)
          .reduce((acc, curr) => acc + curr, 0),
      },
    ].filter((item) => !!item.price),
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
};

export type TempItemDetailsType = Partial<AccessProductType> & {
  offering_id?: string;
  isOtherOption?: boolean;
  provider?: string;
  image?: string | null;
  [key: string]: unknown;
};

export const getNonRecurringCharges = (items: ICartItem[]) => {
  const services = getServiceList(items);
  const mobilityFormData = getMobilityFormData(items);
  const {
    totalServiceActivation,
    totalEquipmentBundle,
    totalStarlinkServiceActivation,
    totallInstallAddOn,
  } = items.reduce(
    (totals, item) => {
      const itemDetails = item.item_details as TempItemDetailsType | undefined;

      const pricing =
        itemDetails?.formal_broadband_pricing ??
        itemDetails?.formal_dia_pricing;

      const serviceActivation = Number(pricing?.service_activation ?? 0);
      const bundle = itemDetails?.bundle;
      const equipmentBundle = Number(
        //@ts-expect-error err
        itemDetails?.formal_starlink_pricing?.[bundle] ?? 0,
      );

      const starlinkServiceActivation =
        itemDetails?.formal_starlink_pricing?.activation ?? 0;

      const isGraniteInstall = itemDetails?.graniteInstall;
      const installAddOn = isGraniteInstall
        ? itemDetails?.formal_starlink_pricing?.installation ?? 0
        : 0;

      return {
        totalServiceActivation:
          totals.totalServiceActivation + serviceActivation,
        totalEquipmentBundle: totals.totalEquipmentBundle + equipmentBundle,
        totalStarlinkServiceActivation:
          totals.totalStarlinkServiceActivation +
          Number(starlinkServiceActivation),
        totallInstallAddOn: totals.totallInstallAddOn + installAddOn,
      };
    },
    {
      totalServiceActivation: 0,
      totalEquipmentBundle: 0,
      totalStarlinkServiceActivation: 0,
      totallInstallAddOn: 0,
    },
  );

  const grandTotal =
    totalServiceActivation +
    totalEquipmentBundle +
    totalStarlinkServiceActivation +
    totallInstallAddOn +
    mobilityFormData.nrc.reduce((sum, item) => sum + Number(item.price), 0);

  return {
    grandTotal,
    charges: [
      ...(services.some((service) => ['Broadband', 'DIA'].includes(service))
        ? [{ name: 'Activation fee', price: totalServiceActivation }]
        : []),
      ...(services.includes('Starlink')
        ? [
            {
              name: 'Starlink activation fee',
              price: totalStarlinkServiceActivation,
            },
            {
              name: 'Equipment bundle',
              price: totalEquipmentBundle,
            },
            {
              name: 'Install add-on',
              price: totallInstallAddOn,
            },
          ]
        : []),
      ...mobilityFormData.nrc,
    ],
  };
};

export const getRecurringCharges = (items: ICartItem[]) => {
  const services = getServiceList(items);
  const mobilityFormData = getMobilityFormData(items);
  const serviceTotals: Record<string, number> = {};

  items.forEach((item) => {
    const itemDetails = item.item_details as TempItemDetailsType | undefined;
    const pricing =
      itemDetails?.formal_broadband_pricing ?? itemDetails?.formal_dia_pricing;

    const serviceType = itemDetails?.product_type as string;
    if (!serviceType) return;

    const circuit = Number(pricing?.circuit ?? 0);

    if (!serviceTotals[serviceType]) {
      serviceTotals[serviceType] = 0;
    }
    serviceTotals[serviceType] += circuit;
  });

  const totalInternetAccessRecovery = items.reduce((sum, item) => {
    const pricing =
      item.item_details?.formal_broadband_pricing ??
      item.item_details?.formal_dia_pricing;
    //@ts-expect-error err
    return sum + Number(pricing?.internet_access_recovery ?? 0);
  }, 0);

  const totalCarrierSurchargeRecovery = items.reduce((sum, item) => {
    const pricing =
      item.item_details?.formal_broadband_pricing ??
      item.item_details?.formal_dia_pricing;
    //@ts-expect-error err
    return sum + Number(pricing?.carrier_surcharge_recovery ?? 0);
  }, 0);

  const totalIP = items.reduce((sum, item) => {
    const pricing =
      item.item_details?.formal_broadband_pricing ??
      item.item_details?.formal_dia_pricing;
    return sum + Number(pricing?.ip ?? 0);
  }, 0);

  const totalMonitoring = items.reduce((sum, item) => {
    const itemDetails = item.item_details as TempItemDetailsType | undefined;
    const pricing =
      itemDetails?.formal_broadband_pricing ?? itemDetails?.formal_dia_pricing;

    const monitoring = itemDetails?.mns_enhanced
      ? pricing?.mns_enhanced
      : itemDetails?.mns_essential
        ? pricing?.mns_essentials
        : 0;

    return sum + Number(monitoring ?? 0);
  }, 0);

  const serviceCharges = Object.entries(serviceTotals)
    .filter(([service]) => service !== 'Starlink')
    .map(([service, price]) => ({
      name: `${service} connection charge`,
      price,
    }));

  const totalModem = items.reduce((sum, item) => {
    const pricing =
      item.item_details?.formal_broadband_pricing ??
      item.item_details?.formal_dia_pricing;
    const modem = pricing?.modem ? Number(pricing?.modem) : 0;
    return sum + Number(modem);
  }, 0);

  const totalMainServicePlan = items.reduce((sum, item) => {
    const pricing = item.item_details?.formal_starlink_pricing;
    const mainServicePlan = pricing?.total_mrc ?? 0;
    return sum + Number(mainServicePlan);
  }, 0);

  const grandTotal =
    Object.entries(serviceTotals)
      .filter(([service]) => service !== 'Starlink')
      .reduce((total, [, price]) => total + price, 0) +
    totalInternetAccessRecovery +
    totalCarrierSurchargeRecovery +
    totalIP +
    totalMonitoring +
    totalModem +
    totalMainServicePlan +
    mobilityFormData.mrc.reduce((sum, item) => sum + Number(item.price), 0);

  return {
    grandTotal,
    charges: [
      ...serviceCharges,
      ...(services.includes('Broadband')
        ? [
            {
              name: 'Internet access recovery',
              price: totalInternetAccessRecovery,
            },
          ]
        : []),
      ...(services.includes('DIA')
        ? [
            {
              name: 'Carrier surcharge recovery',
              price: totalCarrierSurchargeRecovery,
            },
          ]
        : []),
      ...(services.some((service) => ['Broadband', 'DIA'].includes(service))
        ? [
            { name: 'IP blocks', price: totalIP },
            { name: 'Network monitoring', price: totalMonitoring },
            { name: 'Equipment', price: totalModem },
          ]
        : []),
      ...(services.includes('Starlink')
        ? [
            {
              name: 'Main service plan',
              price: totalMainServicePlan,
            },
          ]
        : []),
      ...mobilityFormData.mrc,
    ],
  };
};

export const getTotalMRCAndNRC = (item: ICartItem) => {
  if (item.category === 'Mobility') {
    const MRCAndNRC = getMobilityMRCAndNRC({ device: item.item_details });

    return {
      mrc: MRCAndNRC.total.mrc ?? MRCAndNRC.total.nrc ?? 0,
      nrc: MRCAndNRC.total.nrc ?? 0,
    };
  }

  if (item.category === 'Access') {
    return {
      mrc: getRecurringCharges([item]).grandTotal ?? 0,
      nrc: getNonRecurringCharges([item]).grandTotal ?? 0,
    };
  }

  return {
    mrc: 0,
    nrc: item.price ?? 0,
  };
};

export const buildItemPayload = (item: ICartItem) => {
  return {
    type: item.category ?? '',
    subtype: item.subtype,
    pim_price_id: null,
    cw_ticket_id: item.cw_ticket_id,
    sf_id: item.sf_id,
    cornerstone_id: item.cornerstone_id,
    status: item.status,
    item_details: toJS(item.item_details) ?? [],
    price: item.price ?? 0,
    quantity: item.quantity ?? 0,
    location_id: String(item.locationId ?? ''),
  };
};

const isMobilityPrimaryItem = (item: ICartItem) =>
  item.category === 'Mobility' && !!item?.item_details?.referenceId;

export const isNetworkProduct = (item: ICartItem) => {
  const networkCategories = [...Object.values(NetworkDeviceType)].map(String);
  return networkCategories.includes(
    String(item?.item_details?.productDescType),
  );
};

export const isCellularProduct = (item: ICartItem) => {
  const cellularCategories = [...Object.values(CellularDeviceType)].map(String);
  return cellularCategories.includes(
    String(item?.item_details?.productDescType),
  );
};

export const hasAnyNetworkProduct = (items: ICartItem[]) =>
  items.some((item) => isNetworkProduct(item));

export const hasOnlyPrimaryCellularProducts = (items: ICartItem[]) => {
  const primiryItems = items.filter(isMobilityPrimaryItem);
  return (
    primiryItems.length > 0 &&
    primiryItems.every((item) => isCellularProduct(item))
  );
};
